import React from 'react';
import {
  Layout,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Maximize2,
  Minimize2,
  Grid,
  MoveVertical,
} from 'lucide-react';
import { PageSection, ComponentStyle } from '../../../types';

interface LayoutTabProps {
  section: PageSection;
  onUpdateStyle: (updates: Partial<ComponentStyle>) => void;
}

export const LayoutTab: React.FC<LayoutTabProps> = ({ section, onUpdateStyle }) => {
  const style = section.style || {};

  return (
    <div className="space-y-6">
      {/* Section Height / Padding */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <MoveVertical className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Bölüm Dikey Yüksekliği (Padding)</h4>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'compact', label: 'Kompakt', desc: 'Dar boşluk (py-12)' },
            { id: 'normal', label: 'Standart', desc: 'Dengeli boşluk (py-20)' },
            { id: 'spacious', label: 'Ferah / Geniş', desc: 'Yüksek boşluk (py-28)' },
            { id: 'fullscreen', label: 'Tam Ekran', desc: 'Ekranı kapla (min-h-screen)' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onUpdateStyle({ sectionHeight: opt.id as any })}
              className={`p-2.5 rounded-xl border text-left transition ${
                (style.sectionHeight || 'normal') === opt.id
                  ? 'bg-blue-600/20 border-blue-500 text-white shadow-sm ring-1 ring-blue-500/30'
                  : 'bg-slate-900/80 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <div className="text-xs font-bold">{opt.label}</div>
              <div className="text-[10px] text-slate-400">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Grid Columns */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Grid className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Kart Kolon Sayısı (Masaüstü)</h4>
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          {[
            { cols: 1, label: '1 Kolon' },
            { cols: 2, label: '2 Kolon' },
            { cols: 3, label: '3 Kolon' },
            { cols: 4, label: '4 Kolon' },
          ].map((opt) => (
            <button
              key={opt.cols}
              type="button"
              onClick={() => onUpdateStyle({ columns: opt.cols })}
              className={`py-2 px-1 rounded-xl text-xs font-bold transition text-center ${
                (style.columns || 3) === opt.cols
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Text Alignment */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Layout className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Başlık ve Metin Hizalaması</h4>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { id: 'left', label: 'Sola Yasla', icon: AlignLeft },
            { id: 'center', label: 'Ortala', icon: AlignCenter },
            { id: 'right', label: 'Sağa Yasla', icon: AlignRight },
          ].map((opt) => {
            const Icon = opt.icon;
            const isSelected = (style.alignment || 'center') === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onUpdateStyle({ alignment: opt.id as any })}
                className={`py-2.5 px-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition ${
                  isSelected
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Container Max Width */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Maximize2 className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">İçerik Genişliği</h4>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          {[
            { id: 'compact', label: 'Kompakt (4XL)' },
            { id: 'standard', label: 'Standart (6XL)' },
            { id: 'wide', label: 'Geniş (7XL)' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onUpdateStyle({ containerWidth: opt.id as any })}
              className={`py-2 px-1 rounded-xl text-[10px] font-bold transition text-center ${
                (style.containerWidth || 'wide') === opt.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
